import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ContentLayout, SpaceBetween, Select } from '@cloudscape-design/components';
import { useConfigs } from '@features/configs';
import { useConfigCounts } from '@features/configs/hooks/use-config-counts';
import { ConfigTable } from '@features/configs/components/ConfigTable';
import { TypeFilterTabs } from '@features/configs/components/TypeFilterTabs';
import { BulkActionsToolbar } from '@features/configs/components/BulkActionsToolbar';
import { ConfigurationObject } from '@features/configs/services/configs-api';
import { DeleteConfirmationModal } from '@features/configs/components/DeleteConfirmationModal';

/**
 * Configuration list page with type filter tabs, bulk actions, and sortable table.
 * Requirements: 2.1–2.7, 7.1–7.12, BR-15
 */
export function ConfigListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedType = searchParams.get('type') ?? null;

  const { data, isLoading, error, refetch } = useConfigs();
  const { data: countsData } = useConfigCounts();
  const [deleteTarget, setDeleteTarget] = useState<ConfigurationObject | null>(null);
  const [selectedItems, setSelectedItems] = useState<ConfigurationObject[]>([]);
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string | null>(null);

  // Extract unique business units for the filter dropdown
  const businessUnits = useMemo(() => {
    if (!data?.items) return [];
    const units = new Set<string>();
    for (const item of data.items) {
      if (item.businessUnit) units.add(item.businessUnit);
    }
    return Array.from(units).sort().map((u) => ({ label: u, value: u }));
  }, [data]);

  const handleTypeChange = (type: string | null) => {
    if (type) {
      setSearchParams({ type });
    } else {
      setSearchParams({});
    }
  };

  // Filter items by selected type tab and business unit
  let filteredItems = selectedType
    ? (data?.items ?? []).filter((item) => item.configType === selectedType)
    : (data?.items ?? []);

  if (businessUnitFilter) {
    filteredItems = filteredItems.filter((item) => item.businessUnit === businessUnitFilter);
  }

  const handleDelete = (config: ConfigurationObject) => {
    setDeleteTarget(config);
  };

  return (
    <ContentLayout>
      <SpaceBetween size="m">
        <TypeFilterTabs
          counts={countsData?.counts ?? {}}
          total={countsData?.total ?? data?.count ?? 0}
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
        />

        {businessUnits.length > 0 && (
          <Select
            selectedOption={businessUnitFilter ? { label: businessUnitFilter, value: businessUnitFilter } : null}
            onChange={({ detail }) => setBusinessUnitFilter(detail.selectedOption?.value ?? null)}
            options={[{ label: 'All Business Units', value: '' }, ...businessUnits]}
            placeholder="Filter by Business Unit..."
            filteringType="auto"
          />
        )}

        {selectedItems.length > 0 && (
          <BulkActionsToolbar
            selectedItems={selectedItems}
            onClearSelection={() => setSelectedItems([])}
            onBulkComplete={() => { setSelectedItems([]); refetch(); }}
          />
        )}

        <ConfigTable
          items={filteredItems}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          onDelete={handleDelete}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
        />
      </SpaceBetween>
      {deleteTarget && (
        <DeleteConfirmationModal
          config={deleteTarget}
          visible={!!deleteTarget}
          onDismiss={() => setDeleteTarget(null)}
        />
      )}
    </ContentLayout>
  );
}
